import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Refinement, SearchContext } from './shared/search-context';
import { BehaviorSubject, Observable } from 'rxjs';
import { Result } from './shared/result';
import { Bucket, Facet, Facets } from "./shared/facets";
import { ConfigService } from "./config.service";
import { SwirlResponse, SwirlService } from "./swirl.service";
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  private readonly RESULT_MIXER_KEY = 'result_mixer';
  private readonly DEFAULT_RESULT_MIXER_VALUE = 'RelevancyConfidenceMixer';
  private readonly DEFAULT_RESULT_MIXER = this.RESULT_MIXER_KEY + ':' + this.DEFAULT_RESULT_MIXER_VALUE;

  // Configuration values for minimum scores
  minimumConfidenceScore: number = 0.7;
  minimumSwirlScore: number = 100;

  // Rag Search results
  ragResults = new BehaviorSubject<any>(null);
  isRagLoading = new BehaviorSubject<boolean>(false);
  isRagShowing = new BehaviorSubject<boolean>(false);
  aiModel = new BehaviorSubject<any>(null);
  restResponseTime = new BehaviorSubject<any>(null);

  // Initial search context
  private defaultContext = new SearchContext();
  private searchContextSource = new BehaviorSubject<SearchContext>(this.defaultContext);
  currentSearchContext = this.searchContextSource.asObservable();

  private defaultResult = { 'docs': [], maxScore: 0, numFound: 0, start: 0, numRetrieved: 0 };
  private resultSource = new BehaviorSubject<Result>(this.defaultResult);
  currentResults = this.resultSource.asObservable();

  private defaultSearchInfo = {};
  public searchInfo = new BehaviorSubject<any>(this.defaultSearchInfo);
  currentSearchInfo = this.searchInfo.asObservable();

  private logoClickEvent = false;
  private _logoClickEvent = new BehaviorSubject<any>(this.logoClickEvent);
  eraseEvent = this._logoClickEvent.asObservable();

  // Use plain object for header instead of SolrHeader
  private defaultHeader = { QTime: 0, status: 0, date_retrieved: null };
  private resultHeaderSource = new BehaviorSubject<any>(this.defaultHeader);
  currentResultHeader = this.resultHeaderSource.asObservable();

  private defaultHighlights = {};
  private highlightsSource = new BehaviorSubject<Object>(this.defaultHighlights);
  currentHighlights = this.highlightsSource.asObservable();

  private firstFacets = { count: 0 };
  private defaultFacets = { count: 0 };
  private facetsSource = new BehaviorSubject<Facets>(this.defaultFacets);
  currentFacets = this.facetsSource.asObservable();

  private defaultError = undefined;
  private errorSource = new BehaviorSubject<any>(this.defaultError);
  currentError = this.errorSource.asObservable();

  private loading = new BehaviorSubject<boolean>(false);
  isLoading = this.loading.asObservable();

  private cardView = new BehaviorSubject<boolean>(true);
  isCardView = this.cardView.asObservable();

  private searched = new BehaviorSubject<boolean>(false);
  isSearched = this.searched.asObservable();

  private mixer = new BehaviorSubject<string>('RelevancyMixer');
  resultMixer = this.mixer.asObservable();

  public selectedItems = new BehaviorSubject<boolean>(false);
  isSelectedItems = this.selectedItems.asObservable();

  public _selectedRagItems = new BehaviorSubject<Array<any>>([]);
  selectedRagItems = this._selectedRagItems.asObservable();

  public _ragItems = new BehaviorSubject<any>({});
  ragItems = this._ragItems.asObservable();

  public newSearch = new BehaviorSubject<boolean>(false);
  isNewSearch = this.newSearch.asObservable();

  public enabledCharts = new BehaviorSubject<boolean>(false);
  isEnabledCharts = this.enabledCharts.asObservable();

  public cleanAllToggle = new BehaviorSubject<boolean>(false);
  isCleanAllToggle = this.cleanAllToggle.asObservable();

  private readonly confidenceAvailable: boolean = false;

  private hasSearchTimeoutSource = new BehaviorSubject<boolean>(false);
  hasSearchTimeout = this.hasSearchTimeoutSource.asObservable();

  private rerankSearch(searchId: string | number): Observable<any> {
    const url = `/api/swirl/search/${searchId}/rerank/`;

    // Use the same token mechanism as the rest of the app
    const token = this.authService.getToken();

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Token ${token}`);
    }

    // Backend doesn't care about body shape here; empty object is fine
    return this.http.post(url, {}, { headers });
  }

  // Removed SolrService from the constructor—only using SwirlService now.
  constructor(private swirlService: SwirlService, private configService: ConfigService, private authService: AuthService, private http: HttpClient) {
    // Initialize enabledCharts based on localStorage
    const storedValue = localStorage.getItem('isEnabledCharts');
    const initialState = storedValue !== null ? JSON.parse(storedValue) : false; // Default to false
    this.enabledCharts = new BehaviorSubject<boolean>(initialState);
    this.isEnabledCharts = this.enabledCharts.asObservable();
    this.confidenceAvailable = this.authService.getConfidenceMixerStatus();

    configService.getConfig().subscribe(configResponse => {
      if (configResponse) {
        this.defaultContext = SearchContext.newSearchContext(configResponse);
        this.searchContextSource.next(this.defaultContext);
        
        // Update minimum scores from config
        this.minimumConfidenceScore = configResponse.minimumConfidenceScore ?? this.minimumConfidenceScore;
        this.minimumSwirlScore = configResponse.minimumSwirlScore ?? this.minimumSwirlScore;
      }
    });
  }

  setRagLoading(isLoading: boolean) {
    this.isRagLoading.next(isLoading);
  }

  setRagShowing(isShowing: boolean) {
    this.isRagShowing.next(isShowing);
  }
  
  clearRequestSpecs() { 
    this.aiModel.next(null);
    this.restResponseTime.next(null);
  }

  setRagResults(results: any) {
    this.ragResults.next(results);
  }

  updateEnabledChartsState(state: boolean): void {
    this.enabledCharts.next(state);
    localStorage.setItem('isEnabledCharts', JSON.stringify(state));
  }

  addToSelectedItems(item: any, isClicked: any, score: any) {
    let currentItems = this._selectedRagItems.value;
    const shouldAdd = this._ragItems.value[item] == undefined ? score > 50 : this._ragItems.value[item];
    if (shouldAdd || isClicked) {
      currentItems.push(item);
      currentItems = [...new Set(currentItems)];
      currentItems.sort((a, b) => a - b);
      this._selectedRagItems.next(currentItems);
      this._ragItems.next({
        ...this._ragItems.value,
        [item]: true
      });
      return shouldAdd || isClicked || score > 50;
    }
    return false;
  }

  // Decide whether this result should get a “star”
  shouldSelectItem(doc: any): boolean {
    const mixer = this.mixer.value;
    const confidenceOK = (doc['swirl_confidence_score'] ?? 0) > this.minimumConfidenceScore;
    const relevancyOK  = (doc['swirl_score'] ?? 0) > this.minimumSwirlScore;

    // 1. Confidence mixer → always use confidence threshold
    if (mixer === 'RelevancyConfidenceMixer') {
      return confidenceOK;
    }

    // 2. Relevancy mixer → always use relevancy threshold
    if (mixer === 'RelevancyMixer') {
      return relevancyOK;
    }

    // 3. Any other mixer (Date, Top Pick, future ones)
    return this.confidenceAvailable ? confidenceOK : relevancyOK;
  
  }

  // Add an item to selected items using the consistent selection logic
  addToSelectedItemsWithLogic(doc: any, isClicked: boolean = false): boolean {
    const shouldSelect = this.shouldSelectItem(doc);
    if (shouldSelect || isClicked) {
      return this.addToSelectedItems(doc['swirl_id'], isClicked, doc['swirl_score']);
    }
    return false;
  }

  removeFromSelectedItems(item: any) {
    const currentItems = this._selectedRagItems.value;
    const updatedItems = currentItems.filter((i) => i !== item);
    this._selectedRagItems.next(updatedItems);
    this._ragItems.next({
      ...this._ragItems.value,
      [item]: false
    });
  }

  clearSelectedItems() {
    this._selectedRagItems.next([]);
    this._ragItems.next({});
  }

  isItemSelected(item: any) {
    return this._selectedRagItems.value.includes(item);
  }

  clearResults(): void {
    if (this.resultSource.getValue() !== this.defaultResult) {
      this.resultSource.next(this.defaultResult);
    }
  
    if (this.searched.getValue()) {
      this.searched.next(false);
    }
  }

  changeEraseEvent(value: boolean): void {
    this._logoClickEvent.next(value);
    if (value) {
      this.clearResults();
    }
  }

  changeSearch(searchContext: SearchContext) {
    // Guard clause: clear results if query is empty or whitespace.
    if (!searchContext.query || searchContext.query.trim() === '') {
      this.clearResults();
      return;
    }

    this.loading.next(true);
    this.searched.next(true);
    // Always use swirl search now that solr references have been removed.
    this.swirlService.search(searchContext).then((response: Observable<SwirlResponse>) => {
      response.subscribe({
        next: (swirlResponse: SwirlResponse) => {
          this.searchContextSource.next(searchContext);
          const result = new Result();
          swirlResponse.results?.forEach((sResult: any) => {
            if (sResult.title && sResult.title.length > 0) {
              result.docs.push(sResult);
            } else {
              swirlResponse.info.results.retrieved = swirlResponse?.info?.results?.retrieved - 1 || 0;
              swirlResponse.info.results.retrieved_total = swirlResponse?.info?.results?.retrieved_total - 1 || 0;
              if (swirlResponse.info[sResult.searchprovider]) {
                if (swirlResponse.info[sResult.searchprovider].retrieved == 1) {
                  delete swirlResponse.info[sResult.searchprovider];
                } else {
                  swirlResponse.info[sResult.searchprovider].retrieved = swirlResponse.info[sResult.searchprovider].retrieved - 1;
                }
              }
            }
          });
          this.searchInfo.next(swirlResponse?.info);
          result.numFound = swirlResponse.info?.results?.found_total as number;
          result.numRetrieved = swirlResponse.info?.results?.retrieved_total as number;
          searchContext.searchId = swirlResponse.info?.search?.id as string;
          let facets = new Facets();

          if (this.firstFacets.count == 0) {
            facets.count = swirlResponse.info?.results?.retrieved_total as number;
            const infos = swirlResponse.info;
            const providerFacet = new Facet();
            providerFacet.buckets = new Array<Bucket>();
            for (let key in infos) {
              if (key !== 'search' && key !== 'results') {
                const bucket = new Bucket();
                const fu = infos[key]?.['filter_url'] as string | null | undefined;
                const idFromUrl = fu ? fu.split('=').at(-1) : undefined;
                bucket.val = idFromUrl ?? key;
                bucket.label = key;
                (bucket as any).timed_out   = !!infos[key]?.timed_out;
                (bucket as any).search_time = infos[key]?.search_time;
                // robust count assignment (prefer retrieved, fallback to found)
                const r = Number(infos[key]?.retrieved ?? 0);
                const f = Number(infos[key]?.found ?? 0);
                bucket.count = r > 0 ? r : (f > 0 ? f : 0);

                providerFacet.buckets.push(bucket);
              }
            }
            facets['Source'] = providerFacet;
            this.firstFacets = facets;
          } else {
            facets = this.firstFacets;
          }
          this.facetsSource.next(facets);

          // —— surface timeout / info messages to the component ——
          result.search = { messages: swirlResponse.messages ?? [] };

          this.resultSource.next(result);
          this.resultHeaderSource.next({ QTime: swirlResponse.info?.results?.federation_time * 1000, status: 0, date_retrieved: swirlResponse.results?.[0]?.date_retrieved });
          this.loading.next(false);
        },
        error: (wrapper: any) => {
          console.log('error', wrapper);
          this.loading.next(false);
        }
      });
    });
  }

  changeQuery(query: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.query = query;
    newSearch.start = 0;
    newSearch.page = 1;
    newSearch.searchId = '';
    this.changeSearch(newSearch);
  }

  changeQueryAndProviders(query: string, providers: Array<string> | Array<number>, connectorsNames: Array<string>) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.query = query;
    newSearch.providers = providers;
    newSearch.refine = [];
    newSearch.connectorsNames = connectorsNames;
    newSearch.start = 0;
    newSearch.page = 1;
    newSearch.searchId = '';
    this.firstFacets = { count: 0 };
    this.facetsSource.next({ count: 0 });
    this.newSearch.next(true);
    this.changeSearch(newSearch);
  }

  changeProviders(providers: Array<string>) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.providers = providers;
    newSearch.start = 0;
    newSearch.page = 1;
    newSearch.searchId = '';
    this.changeSearch(newSearch);
  }

  changePage(page: number, start: number) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.page = page;
    newSearch.start = start;
    this.changeSearch(newSearch);
  }

  changeRows(rows: number) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.rows = rows;
    newSearch.start = 0;
    this.changeSearch(newSearch);
  }

  changeSort(sort: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.sort = sort;
    this.changeSearch(newSearch);
  }

  changeDefType(defType: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.defType = defType;
    this.searchContextSource.next(newSearch);
  }

  refine(refinement: Refinement) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.refine.push(refinement);
    newSearch.start = 0;
    newSearch.page = 1;
    this.changeSearch(newSearch);
  }

  refineMultiple(field: string, refinements: Array<Refinement>) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.refine = newSearch.refine.filter((refinement) => refinement.field != field);
    newSearch.refine = refinements;
    newSearch.start = 0;
    newSearch.page = 1;
    this.changeSearch(newSearch);
  }

  getRefine() {
    return this.searchContextSource.getValue().refine;
  }

  removeRefinement(index: number) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.refine.splice(index, 1);
    newSearch.start = 0;
    newSearch.page = 1;
    this.changeSearch(newSearch);
  }

  resetAll() {
    const newSearch = this.searchContextSource.getValue();
    newSearch.query = "*";
    newSearch.refine = [];
    newSearch.start = 0;
    newSearch.page = 1;
    newSearch.searchId = '';
    this.changeSearch(newSearch);
  }

  resetFacets() {
    const newSearch = this.searchContextSource.getValue();
    newSearch.refine = [];
    newSearch.start = 0;
    newSearch.page = 1;
    newSearch.searchId = '';
    this.changeSearch(newSearch);
  }

  getFilter(key: string) {
    const newSearch = this.searchContextSource.getValue();
    const filter = newSearch.filters.get(key);
    return filter;
  }

  putFilter(key: string, filter: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.filters.set(key, filter);
    newSearch.start = 0;
    newSearch.page = 1;
    if (key === 'result_mixer')
      this.mixer.next(filter?.split(':')?.[1]);
    this.changeSearch(newSearch);
  }

  putFilterWithoutSearch(key: string, filter: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.filters.set(key, filter);
    newSearch.start = 0;
    newSearch.page = 1;
    if (key === 'result_mixer')
      this.mixer.next(filter?.split(':')?.[1]);
  }

  removeFilter(key: string) {
    const newSearch = this.searchContextSource.getValue();
    newSearch.filters.delete(key);
    newSearch.start = 0;
    newSearch.page = 1;
    if (key === this.RESULT_MIXER_KEY) {
      const fallbackMixer = newSearch.filters.get(this.RESULT_MIXER_KEY) || this.DEFAULT_RESULT_MIXER;
      const splitted = fallbackMixer.split(':');
      const mixerValue = splitted[1] || this.DEFAULT_RESULT_MIXER_VALUE;
      this.mixer.next(mixerValue);
    }
    this.changeSearch(newSearch);
  }

  toggleView() {
    this.cardView.next(!this.cardView.getValue());
  }

  setHasSearchTimeout(flag: boolean): void {
    this.hasSearchTimeoutSource.next(flag);
  }

  refresh(): void {
    const ctx = this.searchContextSource.getValue();
    if (!ctx?.searchId) return;

    // Force facet rebuild on refresh so counts/timeouts update
    this.firstFacets = { count: 0 };

    // Ask backend to re-run post_result_processors (re-rank results)
    // Then re-fetch this search so UI sees updated ordering & facets
    this.rerankSearch(ctx.searchId).subscribe({
      next: () => {
        this.changeSearch(ctx);
      },
      error: (err) => {
        // Don't strand the user: still refresh results even if rerank fails
        console.error('Rerank failed, falling back to normal refresh', err);
        this.changeSearch(ctx);
      },
    });
  }

}
